// Pure serialisation helpers for exporting subtitles as SRT / WebVTT, plus a
// client-side download helper. No server dependency (Req 25.6, 25.7).

function pad(n, width = 2) {
  return String(n).padStart(width, '0');
}

// Shared ms-based conversion. `sep` is the separator placed before the
// millisecond component (',' for SRT, '.' for WebVTT).
function formatTimestamp(seconds, sep) {
  const total = Math.max(0, seconds);
  const ms = Math.round(total * 1000);
  const hh = Math.floor(ms / 3600000);
  const mm = Math.floor((ms % 3600000) / 60000);
  const ss = Math.floor((ms % 60000) / 1000);
  const mmm = ms % 1000;
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}${sep}${pad(mmm, 3)}`;
}

// HH:MM:SS,mmm (comma before ms), floored at 0 (Req 25.2).
export function secondsToSrtTimestamp(seconds) {
  return formatTimestamp(seconds, ',');
}

// HH:MM:SS.mmm (dot before ms), floored at 0 (Req 25.2).
export function secondsToVttTimestamp(seconds) {
  return formatTimestamp(seconds, '.');
}

// A cue's end: its end time, else the next subtitle's start (a subtitle
// without an end lasts until the next one), else 3 s after it starts.
const cueEnd = (sorted, i) => {
  const s = sorted[i];
  if (typeof s.endTime === 'number' && s.endTime > s.startTime) return s.endTime;
  return sorted[i + 1] ? sorted[i + 1].startTime : s.startTime + 3;
};

// Serialise subtitles to an SRT string: cues sorted ascending by startTime,
// sequential 1-based index, `start --> end` line, caption text, blank line
// between cues (Req 25.2, 25.3, 25.4).
export function toSrt(subtitles, { field = 'originalText' } = {}) {
  const sorted = [...(subtitles || [])].sort((a, b) => a.startTime - b.startTime);
  return sorted
    .map((s, i) => {
      const idx = i + 1;
      const start = secondsToSrtTimestamp(s.startTime);
      const end = secondsToSrtTimestamp(cueEnd(sorted, i));
      const text = (s[field] || '').trim();
      return `${idx}\n${start} --> ${end}\n${text}\n`;
    })
    .join('\n');
}

// Serialise subtitles to a WebVTT string with a WEBVTT header and `.`-separated
// ms timings (Req 25.2).
export function toVtt(subtitles, { field = 'originalText' } = {}) {
  const sorted = [...(subtitles || [])].sort((a, b) => a.startTime - b.startTime);
  const cues = sorted
    .map((s, i) => {
      const start = secondsToVttTimestamp(s.startTime);
      const end = secondsToVttTimestamp(cueEnd(sorted, i));
      const text = (s[field] || '').trim();
      return `${start} --> ${end}\n${text}\n`;
    })
    .join('\n');
  return `WEBVTT\n\n${cues}`;
}

// Client-side download: Blob + object URL + temporary anchor click + revoke.
export function downloadFile(filename, content, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
